import type { Buyer } from '@src/domains/customers/types/models';
import {
  BuyerProfileVariant,
  resolveBuyerProfileVariant,
} from '@src/domains/customers/core/resolveBuyerProfileVariant';

export const BuyerProfileFieldKind = {
  Email: 'email',
  AccountType: 'account-type',
  FirstName: 'first-name',
  LastName: 'last-name',
  Country: 'country',
  CorporateName: 'corporate-name',
  Document: 'document',
  Phone: 'phone',
  ZipCode: 'zip-code',
} as const;

export type BuyerProfileFieldKind = (typeof BuyerProfileFieldKind)[keyof typeof BuyerProfileFieldKind];

export const BuyerDocumentKind = {
  Cpf: 'cpf',
  Cnpj: 'cnpj',
  NationalId: 'national-id',
  Passport: 'passport',
} as const;

export type BuyerDocumentKind = (typeof BuyerDocumentKind)[keyof typeof BuyerDocumentKind];

export type BuyerAccountType = 'individual' | 'company';

export interface BuyerProfileField {
  kind: BuyerProfileFieldKind;
  /** Raw entity value — masking and translation belong to the feature that renders it. */
  value: string;
  documentKind?: BuyerDocumentKind;
  /** ISO 3166-1 alpha-3, only on the country field. */
  countryCode?: string;
  accountType?: BuyerAccountType;
}

export interface BuyerProfileFields {
  variant: BuyerProfileVariant;
  fields: BuyerProfileField[];
}

const DOCUMENT_KIND_BY_VARIANT: Record<BuyerProfileVariant, BuyerDocumentKind> = {
  [BuyerProfileVariant.DomesticIndividual]: BuyerDocumentKind.Cpf,
  [BuyerProfileVariant.DomesticCompany]: BuyerDocumentKind.Cnpj,
  [BuyerProfileVariant.ForeignNationalId]: BuyerDocumentKind.NationalId,
  [BuyerProfileVariant.ForeignPassport]: BuyerDocumentKind.Passport,
};

/**
 * The buyer data the confirmed purchase shows, in reading order. Fields the buyer never
 * filled in are dropped, which is what makes the block vary per buyer.
 */
export function buildBuyerProfileFields(buyer: Buyer): BuyerProfileFields {
  const variant = resolveBuyerProfileVariant(buyer);
  const isCompany = variant === BuyerProfileVariant.DomesticCompany;

  const candidates: (BuyerProfileField | null)[] = [
    buyer.email ? { kind: BuyerProfileFieldKind.Email, value: buyer.email } : null,
    {
      kind: BuyerProfileFieldKind.AccountType,
      value: isCompany ? 'company' : 'individual',
      accountType: isCompany ? 'company' : 'individual',
    },
    buyer.firstName ? { kind: BuyerProfileFieldKind.FirstName, value: buyer.firstName } : null,
    buyer.lastName ? { kind: BuyerProfileFieldKind.LastName, value: buyer.lastName } : null,
    buyer.countryCode
      ? { kind: BuyerProfileFieldKind.Country, value: buyer.countryCode, countryCode: buyer.countryCode }
      : null,
    buyer.corporateName ? { kind: BuyerProfileFieldKind.CorporateName, value: buyer.corporateName } : null,
    buyer.identity
      ? { kind: BuyerProfileFieldKind.Document, value: buyer.identity, documentKind: DOCUMENT_KIND_BY_VARIANT[variant] }
      : null,
    buyer.phone ? { kind: BuyerProfileFieldKind.Phone, value: buyer.phone } : null,
    buyer.address?.zipCode ? { kind: BuyerProfileFieldKind.ZipCode, value: buyer.address.zipCode } : null,
  ];

  return { variant, fields: candidates.filter((field): field is BuyerProfileField => field !== null) };
}
